import { Middleware } from 'redux'
import io from 'socket.io-client';
import { gameActions } from '../Game/gameSlice';
import { config } from '../../config/config';
import { cardSetActions } from '../Card/cardSetSlice';
import { diceActions } from '../Dice/diceSlice';
import { emojiActions } from '../Emoji/emojiSlice';
import { userActions } from '../User/userSlice';
import { gameListActions } from '../Matchmaking/gameListSlice';

enum ClientEvent {
  JOIN = 'CLIENT_JOIN',
  CREATE_NEW_GAME = 'CLIENT_CREATE_NEW_GAME',
  PLAYER_FORM = 'CLIENT_PLAYER_FORM',
  USER_STATS = 'CLIENT_USER_STATS',
  TOKEN = 'CLIENT_TOKEN',
  LOBBY_SETTINGS = 'LOBBY_SETTINGS',
  START = 'CLIENT_START',
  GAME = 'CLIENT_GAME',
  SENTENCE = 'CLIENT_SENTENCE',
  MATCH = 'CLIENT_MATCH',
  MATCH_SELECT = 'CLIENT_MATCH_SELECT',
  VOTE = 'CLIENT_VOTE',
  FINISHED = 'CLIENT_FINISHED',
  RESTART = 'CLIENT_RESTART',
  STORYTELLER = 'CLIENT_STORYTELLER',
  END_SESSION = 'CLIENT_END_SESSION',
  SAVE_GAME = 'CLIENT_SAVE_GAME',
  DICE = 'CLIENT_DICE',
  EMOJI = 'CLIENT_EMOJI',
  GAME_VOTE_TOGGLE = 'CLIENT_GAME_VOTE_TOGGLE',
  GAME_VOTE_FINISHED = 'CLIENT_GAME_VOTE_FINISHED',
  CARD_VIEW = 'CLIENT_CARD_VIEW',
  CARD_ARROW = 'CLIENT_CARD_ARROW',
  SEND_AVATAR = 'CLIENT_SEND_AVATAR',
  // @TODO remove, not needed
  NOTIFY_START = 'CLIENT_NOTIFY_START',
  COMMENT = 'CLIENT_COMMENT',
  KICK = 'CLIENT_KICK',
  PLAYERS_LIST = 'CLIENT_PLAYERS_LIST',
}

enum ServerEvent {
  PLAYER = 'SERVER_PLAYER',
  USER = 'SERVER_USER',
  USER_STATS = 'SERVER_USER_STATS',
  GAME_LIST = 'SERVER_GAME_LIST',
  TOKEN = 'SERVER_TOKEN',
  WRONG_GAME_ID = 'WRONG_GAME_ID',
  UNKNOWN_TOKEN = 'UNKNOWN_TOKEN',
  GAME = 'SERVER_GAME',
  RESTART = 'SERVER_RESTART',
  CARD_SETS = 'SERVER_CARD_SETS',
  DICE = 'SERVER_DICE',
  DISCONNECT = 'SERVER_DISCONNECT',
  EMOJI = 'SERVER_EMOJI',
  PLAYERS_LIST = 'SERVER_PLAYERS_LIST',
}

const socketMiddleware: Middleware = (store) => {

  let socket: SocketIOClient.Socket;

  const reconnect = (socket: SocketIOClient.Socket) => {
    if (!socket) {
      return;
    }

    // Disconnect the socket
    if (socket.connected) {
      socket.disconnect();
    }

    //Immediately reconnect.
    socket.connect();
  };

  return next => action => {
    const isConnectionEstablished = socket && store.getState().game.isConnected;

    if (gameActions.startConnecting.match(action)) {
      socket = io(config.socketHost, {
        // port 3003
        path: '/dixit-socket/',
        forceNew: true,
      });

      socket.on('connect', () => {
        store.dispatch(gameActions.connectionEstablished());
      });

      socket.on('disconnect', () => {
        store.dispatch(gameActions.disconnect());
      });

      // receive

      socket.on(ServerEvent.GAME, (payload: any) => {
        store.dispatch(gameActions.receiveGame(payload));
      });

      socket.on(ServerEvent.PLAYER, (payload: any) => {
        store.dispatch(gameActions.receivePlayer(payload));
      });

      socket.on(ServerEvent.DISCONNECT, (payload: any) => {
        //socket.disconnect();
        store.dispatch(gameActions.serverDisconnect(payload));
      });

      socket.on(ServerEvent.WRONG_GAME_ID, (payload: any) => {
        store.dispatch(gameActions.wrongGameId(payload));
        reconnect(socket);
      });

      socket.on(ServerEvent.UNKNOWN_TOKEN, (payload: any) => {
        store.dispatch(gameActions.unknownToken(payload));
      });
      
      socket.on(ServerEvent.RESTART, (payload: any) => {
        reconnect(socket);
      });

      socket.on(ServerEvent.TOKEN, (payload: any) => {
        store.dispatch(gameActions.token(payload));
        store.dispatch(gameListActions.receiveGames(payload));
      });

      socket.on(ServerEvent.USER, (payload: any) => {
        store.dispatch(userActions.receiveUser(payload));
      });

      socket.on(ServerEvent.USER_STATS, (payload: any) => {
        store.dispatch(userActions.receiveStats(payload));
      });

      socket.on(ServerEvent.PLAYERS_LIST, (payload: any) => {
        store.dispatch(userActions.receivePlayers(payload));
      });

      socket.on(ServerEvent.GAME_LIST, (payload: any) => {
        store.dispatch(gameListActions.receiveGames(payload));
      });

      socket.on(ServerEvent.CARD_SETS, (payload: any) => {
        store.dispatch(cardSetActions.receiveCardSets(payload));
      });

      socket.on(ServerEvent.DICE, (payload: any) => {
        store.dispatch(diceActions.receiveDice(payload));
      });

      socket.on(ServerEvent.EMOJI, (payload: any) => {
        store.dispatch(emojiActions.receiveEmoji(payload));
      });
    }

    if (gameActions.cleanUpGame.match(action)) {
      reconnect(socket);
    }

    // send
    if (gameActions.join.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.JOIN, {
        token: action.payload.token,
        gameId: action.payload.gameId,
        metadata: {
          userAgent: navigator.userAgent,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
        }
      });
    }

    if (gameActions.sendStart.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.START, action.payload);
    }

    if (gameActions.sendCreateNewGame.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.CREATE_NEW_GAME, action.payload);
    }

    if (gameActions.sendUser.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.PLAYER_FORM, action.payload);
    }

    if (gameActions.sendToken.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.TOKEN, action.payload);
    }

    if (gameActions.requestUserStats.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.USER_STATS, action.payload);
    }

    if (gameActions.requestPlayersList.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.PLAYERS_LIST, action.payload);
    }

    if (gameActions.submitSentence.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.SENTENCE, action.payload);
    }

    if (gameActions.selectMatchCard.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.MATCH_SELECT, action.payload);
    }

    if (gameActions.sendSettings.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.LOBBY_SETTINGS, action.payload);
    }

    if (gameActions.submitMatch.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.MATCH, action.payload);
    }

    if (gameActions.submitFinished.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.FINISHED, action.payload);
    }

    if (gameActions.submitStoryteller.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.STORYTELLER, action.payload);
    }

    if (gameActions.submitVote.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.VOTE, action.payload);
    }

    if (gameActions.submitComment.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.COMMENT, action.payload);
    }

    if (gameActions.sendRestart.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.RESTART, action.payload);
    }

    if (gameActions.sendEndSession.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.END_SESSION, action.payload);
    }

    if (gameActions.sendKick.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.KICK, action.payload);
    }

    if (gameActions.sendNotifyStart.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.NOTIFY_START, action.payload);
    }

    if (gameActions.sendSave.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.SAVE_GAME, action.payload);
    }

    if (gameActions.submitGameVoteFinished.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.GAME_VOTE_FINISHED, action.payload);
    }

    if (gameActions.submitGameVoteToggle.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.GAME_VOTE_TOGGLE, action.payload);
    }

    if (gameActions.sendCardView.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.CARD_VIEW, action.payload);
    }

    if (gameActions.sendCardArrow.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.CARD_ARROW, action.payload);
    }

    if (gameActions.changeAvatar.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.SEND_AVATAR, action.payload);
    }

    if (emojiActions.sendEmoji.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.EMOJI, action.payload);
    }

    if (diceActions.sendDice.match(action) && isConnectionEstablished) {
      socket.emit(ClientEvent.DICE, action.payload);
    }


    next(action);

  }
}

export default socketMiddleware;